import {NgClass} from '@angular/common';
import {Component, Input} from '@angular/core';
import moment from 'moment-timezone';

type CalendarTaskItem = {
  id: string | number;
  name: string;
  priority?: string;
  date?: string;
};

@Component({
  selector: 'core-calendar-widget',
  imports: [NgClass],
  templateUrl: './calendar-widget.html',
  styleUrl: './calendar-widget.scss',
  host: {
    style: 'display: inline-flex;height: 100%;width: 100%;padding: 1rem 1rem;flex-direction: column;gap: 1rem;'
  }
})
export class CalendarWidget {

  @Input()
  title: string = '';

  @Input()
  locale: string = 'es';

  @Input()
  tasks: CalendarTaskItem[] = [];

  calendarDate!: moment.Moment;
  calendarStartDate!: moment.Moment;
  calendarEndDate!: moment.Moment;
  calendarDays: moment.Moment[] = [];
  weekdayNames: string[] = [];

  @Input()
  set date(value: string | undefined) {
    this.calendarDate = moment(value || undefined);
    this.calculateCalendarDates();
  }

  get currentMonthLabel(): string {
    return this.calendarDate?.clone().locale(this.locale).format('MMMM YYYY') || '';
  }

  goToPreviousMonth(): void {
    this.calendarDate = this.calendarDate.clone().subtract(1, 'month');
    this.calculateCalendarDates();
  }

  goToNextMonth(): void {
    this.calendarDate = this.calendarDate.clone().add(1, 'month');
    this.calculateCalendarDates();
  }

  getTasksForDay(day: moment.Moment): CalendarTaskItem[] {
    const targetDay = day.format('YYYY-MM-DD');
    return this.tasks.filter(task => {
      if (!task.date) return false;
      return moment(task.date).format('YYYY-MM-DD') === targetDay;
    });
  }

  getPriorityDotClass(priority?: string): string {
    switch (priority) {
      case 'high':
        return 'priority-dot-high';
      case 'low':
        return 'priority-dot-low';
      case 'medium':
      default:
        return 'priority-dot-medium';
    }
  }

  private calculateCalendarDates(): void {
    moment.locale(this.locale);

    const firstDayOfMonth = this.calendarDate.clone().startOf('month');

    // Find the Monday before or on the first day of the month
    this.calendarStartDate = firstDayOfMonth.clone();
    while (this.calendarStartDate.day() !== 0) {
      this.calendarStartDate.subtract(1, 'days');
    }

    const lastDayOfMonth = this.calendarDate.clone().endOf('month');

    // Find the Sunday after or on the last day of the month
    this.calendarEndDate = lastDayOfMonth.clone();
    while (this.calendarEndDate.day() !== 6) {
      this.calendarEndDate.add(1, 'days');
    }

    this.calendarDays = [];
    const currentDay = this.calendarStartDate.clone();
    while (currentDay.isSameOrBefore(this.calendarEndDate, 'day')) {
      this.calendarDays.push(currentDay.clone());
      currentDay.add(1, 'days');
    }

    // Get weekday names starting from Monday
    this.weekdayNames = [];
    for (let i = 1; i <= 7; i++) {
      this.weekdayNames.push(moment().day(i).format('dddd'));
    }
  }
}
